import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, forkJoin, of, throwError } from "rxjs";
import { Cart, CartItem, CartSearchModel, CartShareResolution, CartShareResult } from "./models/cart.model";
import { isWeeklyDuration } from "./rental-duration-billing";
import { ToastrService } from "ngx-toastr";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import {
  BaseApiService,
  AuthenticationService,
  LocalStorageService,
  LocalStorageKeys,
  SearchResult,
  EnvironmentConfig,
  ErrorsHandler,
  LogService,
  MarketTrackingEvents,
  DateTimeUtils,
  Location,
} from "../core/core";
import { TransportationInfo, JobsiteContact } from "../transportation/transportation";
import { User } from "oidc-client";
import { switchMap, tap, map, catchError } from "rxjs/operators";
import { formatTransportationAddress, OpenAIService } from "../rubbl-shared/services/open-ai.service";
import { InventoryItem } from "../inventory/inventory";

declare var environment: EnvironmentConfig;

/** The API returns a renter's active carts in id order, so pick the most recently touched one. */
export function newestCart(carts: Cart[]): Cart {
  const touched = (cart: Cart) => Date.parse(cart?.auditMetadata?.modifiedDateTime || cart?.auditMetadata?.createdDateTime || "") || 0;
  return [...(carts || [])].sort((a, b) => touched(b) - touched(a))[0];
}

@Injectable({
  providedIn: "root",
})
export class CartService extends BaseApiService<Cart> {
  user: User;

  cart: Cart = new Cart();
  cartBSubject: BehaviorSubject<Cart> = new BehaviorSubject<Cart>(this.cart);

  /**
   * False until `init()` finishes hydrating from the API (or determines there is no cart).
   * The subject starts with an empty `new Cart()`, so consumers must not treat that first
   * emission as “the cart is empty”.
   */
  cartHydratedBSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private toastrService: ToastrService,
    private localStorageService: LocalStorageService,
    httpClient: HttpClient,
    private authenticationService: AuthenticationService,
    private errorsHandler: ErrorsHandler,
    private logService: LogService,
    private openAIService: OpenAIService,
  ) {
    super(httpClient, environment.ordersApiDomain, "/api/orders/v2/cart", true, authenticationService);

    if (environment.clientId !== "rubbl.website") {
      this.markCartHydrated();
      return;
    }

    this.init();
  }

  init() {
    this.authenticationService
      .getUser()
      .then(user => {
        const cartIdFromLocalStorage = this.localStorageService.get(LocalStorageKeys.CartId);
        // if no user but there is a cart id in local storage, we need to get it from api
        if (!user) {
          if (cartIdFromLocalStorage) {
            this.getWithPartitionKey(cartIdFromLocalStorage, "00000000-0000-0000-0000-000000000000", "").subscribe({
              next: cart => this.updateSubscribers(cart),
              error: () => this.markCartHydrated(),
              complete: () => this.markCartHydrated(),
            });
            return;
          }
          this.markCartHydrated();
          return;
        }

        // user logged in! grab cart by user id
        this.user = user;
        this.getCartByUserId(user.profile.sub).subscribe({
          next: cartSearchResult => {
            // if we couldnt find cart on api and there is a local storage cart lets tie the user to this cart
            if (!cartSearchResult?.results?.length) {
              if (cartIdFromLocalStorage) {
                this.updateCartUser(cartIdFromLocalStorage, user.profile.sub, user.profile.account_id).subscribe({
                  next: userCart => this.updateSubscribers(userCart),
                  error: () => this.markCartHydrated(),
                  complete: () => this.markCartHydrated(),
                });
                this.localStorageService.set(LocalStorageKeys.CartId, "");
                return;
              }
              this.markCartHydrated();
              return;
            }

            // we found a cart from the api
            const cart = newestCart(cartSearchResult.results);
            this.updateSubscribers(cart);

            // if there isnt a cart id in local storage do not merge the carts
            if (!cartIdFromLocalStorage) {
              return;
            }
            // there is a cart from the api and a cart id in local storage we need to merge the two carts
            this.getWithPartitionKey(cartIdFromLocalStorage, "00000000-0000-0000-0000-000000000000", "").subscribe(localStorageCart =>
              this.mergeCarts(localStorageCart, cart),
            );
          },
          error: () => this.markCartHydrated(),
        });
      })
      .catch(() => this.markCartHydrated());
  }

  getWithoutRebuild(id: string): Observable<Cart> {
    const headers = this.getAuth();
    let params = new HttpParams();
    params = params.append("skipCartRebuild", true);

    return this.httpClient.get<Cart>(`${this.baseUrl}${this.endpoint}/${id}`, { headers, params });
  }

  mergeCarts(localStorageCart: Cart, apiCart: Cart) {
    // find cart items that do exist on the local storage cart, but do not exist on the api cart
    const cartItemsToAdd = [];
    for (const cartItem of localStorageCart.items) {
      if (!apiCart.items.some(d => d.inventoryItem.id === cartItem.inventoryItem.id)) {
        const cartItemToAdd = new CartItem(
          new Date(cartItem.beginDate),
          DateTimeUtils.calcMonthDurationFromDays(cartItem.duration),
          cartItem.inventoryItem,
          [],
          cartItem.rpoInterested,
          undefined, // jobZipCode
          cartItem.imageThumbnail, // preserve existing thumbnail when merging carts
        );
        cartItemsToAdd.push(cartItemToAdd);
      }
    }

    // add cart items to existing cart (no toast, no per-item add analytics — merge, not a user "add" action)
    if (cartItemsToAdd.length > 0) {
      this.addCartItems(cartItemsToAdd, false, false).subscribe();
    }

    // delete old local storage cart
    this.delete(localStorageCart.id, "partitionKey", null, true).subscribe(() => this.localStorageService.set(LocalStorageKeys.CartId, ""));
  }

  updateCartUser(cartId: string, userId: string, accountId: string) {
    let params = new HttpParams();
    params = params.append("partitionKey", "00000000-0000-0000-0000-000000000000");
    const jsonPatchOperation = [
      { op: "replace", path: "/userId", value: userId },
      { op: "replace", path: "/accountId", value: accountId },
    ];

    return this.httpClient.patch<Cart>(`${this.baseUrl}${this.endpoint}/${cartId}`, jsonPatchOperation, {
      params,
    });
  }

  getCartByUserId(userId: string): Observable<SearchResult<Cart>> {
    const searchModel = new CartSearchModel();
    searchModel.userId = userId;
    return this.httpClient.get<SearchResult<Cart>>(`${this.baseUrl}${this.endpoint}`, {
      params: searchModel as any,
    });
  }

  isInventoryItemInCart(inventoryItemId: string): boolean {
    if (!this.cart) {
      return false;
    }

    return this.cart.items.some(d => d.inventoryItem.id === inventoryItemId);
  }

  getItemByInventoryItemId(inventoryItemId: string): CartItem {
    if (!this.cart) {
      return null;
    }

    return this.cart.items.find(d => d.inventoryItem.id === inventoryItemId);
  }

  getCartItemEstimate(cartItem: CartItem, couponCode?: string): Observable<CartItem> {
    let url = `${this.baseUrl}${this.endpoint}/00000000-0000-0000-0000-000000000000/item/estimate`;
    if (couponCode) {
      url += `?couponCode=${encodeURIComponent(couponCode)}`;
    }
    return this.httpClient.post<CartItem>(url, cartItem);
  }

  getOtherAttachmentIds(machineIdToExclude: string): string[] {
    if (this.cart == null || this.cart?.items.length == 0) {
      return [];
    }

    let machineIdsInCart = [];

    for (const item of this.cart.items.filter(d => d.inventoryItem.id != machineIdToExclude)) {
      for (const attachment of item.attachments) {
        machineIdsInCart.push(attachment.childItemId);
      }
    }

    return machineIdsInCart;
  }

  addItemToCart(cartItem: CartItem, showAddToast: boolean = true): Observable<Cart> {
    if (this.isInventoryItemInCart(cartItem.inventoryItem.id)) {
      return of(this.cart);
    }

    this.logService.trackEvent(MarketTrackingEvents.Cart.TryAddItem, { inventoryId: cartItem.inventoryItem.id });

    if (this.cart == null || !this.cart.id || !this.cart.items?.length || this.cart.activeStatus === "Inactive") {
      this.cart = new Cart();
      // create cart if there is no usable current cart (no id, no items, or Inactive)
      this.cart.items = [cartItem];
      if (this.user) {
        this.cart.userId = this.user.profile.sub;
        this.cart.accountId = this.user.profile.account_id;
      }

      return this.create(this.cart).pipe(
        tap(cart => {
          this.updateSubscribers(cart);
          this.logService.trackEvent(
            MarketTrackingEvents.Cart.ItemAdded,
            {
              event: "ecommerce",
              ecommerceType: "cart",
              ecommerceAction: "Item added",
              inventoryId: cartItem.inventoryItem.id,
            },
            true,
          );

          if (!this.user) {
            this.localStorageService.set(LocalStorageKeys.CartId, cart.id);
          }

          if (showAddToast) {
            this.toastrService.success("Item added to your cart", null);
          }

          // GA `add_to_cart` + FB `AddToCart` now fire at the Review-button click in
          // the add-to-cart modal (see MachineCardAddToCartComponent.startDateAndDurationCompleted),
          // so we no longer emit them here.
        }),
      );
    }

    return this.addCartItems([cartItem], showAddToast, true);
  }

  addCartItems(cartItems: CartItem[], showToast: boolean, trackEcommerce: boolean = true): Observable<Cart> {
    let params = new HttpParams();
    if (this.user != null) {
      params = params.append("accountId", this.user.profile.account_id);
    }

    return this.httpClient
      .post<Cart>(`${this.baseUrl}${this.endpoint}/${this.cart.id}/item`, cartItems, { params })
      .pipe(
        tap((cart: Cart) => {
          this.updateSubscribers(cart);

          if (showToast) {
            this.toastrService.success("Item added to your cart", null);
          }

          if (trackEcommerce) {
            cartItems
              .map(d => d.inventoryItem.id)
              .forEach((inventoryId: string) =>
                this.logService.trackEvent(
                  MarketTrackingEvents.Cart.ItemAdded,
                  {
                    event: "ecommerce",
                    ecommerceType: "cart",
                    ecommerceAction: "Item added",
                    inventoryId,
                  },
                  true,
                ),
              );

            // GA `add_to_cart` + FB `AddToCart` now fire at the Review-button click in
            // the add-to-cart modal (see MachineCardAddToCartComponent.startDateAndDurationCompleted).
            // Internal `cart-item-added` telemetry above stays for product analytics.
          }
        }),
      );
  }

  updateCartItem(cartItem: CartItem, displayUpdateMessage: boolean = true) {
    let params = new HttpParams();
    if (this.user != null) {
      params = params.append("partitionKey", this.user.profile.account_id);
    }

    // find the index of the cart item
    const index = this.cart.items.findIndex(d => d.id == cartItem.id);
    if (index == -1) {
      return throwError(() => new Error(`Cart item ${cartItem?.id} not found for update`));
    }

    // replace cart item
    this.cart.items[index] = cartItem;
    const jsonPatchOperation = [{ op: "replace", path: "/items", value: this.cart.items }];

    return this.httpClient.patch(`${this.baseUrl}${this.endpoint}/${this.cart.id}`, jsonPatchOperation, { params }).pipe(
      tap((cart: Cart) => {
        this.updateSubscribers(cart);
        this.logService.pushTagToGtm({
          event: "ecommerce",
          ecommerceType: "cart",
          ecommerceAction: "Item updated",
          inventoryItemId: cartItem?.inventoryItem?.id || "",
        });
      }),
    );
  }

  updateCartItemFromAdmin(cartItem: CartItem, cart: Cart) {
    let params = new HttpParams();
    params = params.append("partitionKey", cart.accountId);

    const index = cart.items.findIndex(d => d.id == cartItem.id);
    if (index == -1) {
      return;
    }

    // replace cart item
    cart.items[index] = cartItem;
    const jsonPatchOperation = [
      { op: "replace", path: "/items", value: cart.items },
      { op: "replace", path: "/activeStatus", value: "Active" },
    ];

    // need to SET BACK TO INACTIVE
    return this.httpClient.patch(`${this.baseUrl}${this.endpoint}/${cart.id}`, jsonPatchOperation, { params });
  }

  removeItemFromCart(cartItemId: string): void {
    this.logService.trackEvent(MarketTrackingEvents.Cart.TryRemoveItem);

    const inventoryItemId = this.cart.items.find(d => d.id === cartItemId)?.inventoryItem?.id;
    const isLastItem = this.cart.items.length === 1;

    const removeAction$ = isLastItem
      ? this.delete(this.cart.id, "partitionKey", this.user ? this.user.profile.account_id : null)
      : this.deleteCartItem(cartItemId);

    removeAction$.subscribe((returnedCart: Cart) => {
      if (isLastItem) {
        // Always forget session identity after last-item logical delete — ignore the DELETE body.
        this.wipeCart();
        this.logService.trackEvent(MarketTrackingEvents.Cart.CartDeleted);
      } else {
        this.updateSubscribers(returnedCart);
      }
      this.toastrService.success("Item removed from your cart", null, { positionClass: "toast-top-center" });
      this.logService.trackEvent(
        MarketTrackingEvents.Cart.ItemRemoved,
        {
          event: "ecommerce",
          ecommerceType: "cart",
          ecommerceAction: "Item removed",
          inventoryItemId,
        },
        true,
      );
    });
  }

  deleteCartItem(cartItemId: string) {
    let params = new HttpParams();
    if (this.user != null && this.user.profile != null) {
      params = params.append("partitionKey", this.user.profile.account_id);
    }

    const newCartItems = this.cart.items.filter(d => d.id != cartItemId);
    const jsonPatchOperation = [{ op: "replace", path: "/items", value: newCartItems }];

    return this.httpClient.patch(`${this.baseUrl}${this.endpoint}/${this.cart.id}`, jsonPatchOperation, {
      headers: this.getAuth(),
      params,
    });
  }

  updateCustomerMetadata(phoneNumber: string) {
    let params = new HttpParams();
    if (this.user != null && this.user.profile != null) {
      params = params.append("partitionKey", this.user.profile.account_id);
    }

    const jsonPatchOperation = [{ op: "replace", path: "/customerMetadata/phoneNumber", value: phoneNumber }];
    return this.httpClient.patch(`${this.baseUrl}${this.endpoint}/${this.cart.id}`, jsonPatchOperation, {
      headers: this.getAuth(),
      params,
    });
  }

  addTransportationInfo(transportationInfo: TransportationInfo, poNumber: string): Observable<any> {
    let params = new HttpParams();
    if (this.user != null && this.user.profile != null) {
      params = params.append("partitionKey", this.user.profile.account_id);
    }

    const deliveryAddress = transportationInfo.deliveryAddress;
    const newZipCode = deliveryAddress?.address?.postalCode;
    const jsonPatchOperation = [{ op: "replace", path: "/transportationInfo", value: transportationInfo as any }];

    // Re-estimate when the renter destination (street or ZIP) changed.
    const itemsToUpdate: { index: number; cartItem: CartItem }[] = [];

    for (let i = 0; i < this.cart.items.length; i++) {
      const cartItem = this.cart.items[i];
      const previousDestination = formatTransportationAddress(cartItem.jobLocation, cartItem.jobZipCode);
      const nextDestination = formatTransportationAddress(deliveryAddress, newZipCode);

      if (previousDestination !== nextDestination) {
        itemsToUpdate.push({ index: i, cartItem });
        cartItem.jobZipCode = newZipCode;
        cartItem.jobLocation = deliveryAddress;
      }

      jsonPatchOperation.push({ op: "replace", path: `/items/${i}/jobZipCode`, value: newZipCode });
      jsonPatchOperation.push({ op: "replace", path: `/items/${i}/jobLocation`, value: deliveryAddress as any });
    }

    jsonPatchOperation.push({ op: "replace", path: "/poNumber", value: poNumber });
    
    if (this.cart.jobType) {
      jsonPatchOperation.push({ op: "replace", path: "/jobType", value: this.cart.jobType });
    }

    // If we have items to update with new transportation costs
    if (itemsToUpdate.length > 0) {
      return this.updateTransportationCostsForItems(itemsToUpdate, deliveryAddress || newZipCode).pipe(
        switchMap(updatedItems => {
          // Add transportation estimate updates to the patch operation
          updatedItems.forEach(({ index, transportationEstimate }) => {
            if (transportationEstimate) {
              jsonPatchOperation.push({
                op: "replace",
                path: `/items/${index}/transportationEstimateSummary`,
                value: transportationEstimate,
              });
            }
          });

          return this.httpClient
            .patch(`${this.baseUrl}${this.endpoint}/${this.cart.id}`, jsonPatchOperation, { params })
            .pipe(tap((cart: Cart) => this.updateSubscribers(cart)));
        }),
      );
    }

    // No items need transportation cost updates
    return this.httpClient
      .patch(`${this.baseUrl}${this.endpoint}/${this.cart.id}`, jsonPatchOperation, { params })
      .pipe(tap((cart: Cart) => this.updateSubscribers(cart)));
  }

  addPaymentInfo(paymentContact: JobsiteContact[]): Observable<any> {
    let params = new HttpParams();
    params = params.append("partitionKey", this.user.profile.account_id);

    const jsonPatchOperation = [{ op: "replace", path: "/paymentContacts", value: paymentContact }];

    return this.httpClient
      .patch(`${this.baseUrl}${this.endpoint}/${this.cart.id}`, jsonPatchOperation, { params })
      .pipe(tap((cart: Cart) => this.updateSubscribers(cart)));
  }

  addMetadata(metadata): Observable<any> {
    let params = new HttpParams();
    params = params.append("partitionKey", this.user.profile.account_id);

    const jsonPatchOperation = [{ op: "replace", path: "/metadata", value: metadata }];

    return this.httpClient
      .patch(`${this.baseUrl}${this.endpoint}/${this.cart.id}`, jsonPatchOperation, { params })
      .pipe(tap((cart: Cart) => this.updateSubscribers(cart)));
  }

  updateDeliveryOption(deliveryOption: string): Observable<any> {
    let params = new HttpParams();
    if (this.user != null && this.user.profile != null) {
      params = params.append("partitionKey", this.user.profile.account_id);
    }

    const jsonPatchOperation = [{ op: "replace", path: "/deliveryOption", value: deliveryOption }];

    return this.httpClient
      .patch(`${this.baseUrl}${this.endpoint}/${this.cart.id}`, jsonPatchOperation, { params })
      .pipe(tap((cart: Cart) => this.updateSubscribers(cart)));
  }

  updateJobType(jobType: string): Observable<any> {
    let params = new HttpParams();
    if (this.user != null && this.user.profile != null) {
      params = params.append("partitionKey", this.user.profile.account_id);
    }

    this.cart.jobType = jobType;
    const jsonPatchOperation = [{ op: "replace", path: "/jobType", value: jobType }];

    return this.httpClient
      .patch(`${this.baseUrl}${this.endpoint}/${this.cart.id}`, jsonPatchOperation, { params })
      .pipe(tap((cart: Cart) => this.updateSubscribers(cart)));
  }

  createOrder(cartId: string, paymentContacts: JobsiteContact[], jobNumber: string) {
    return this.httpClient.post<any>(`${this.baseUrl}/api/orders/v2/order`, { cartId: cartId, paymentContacts, jobNumber }, { headers: this.getAuth() });
  }

  /**
   * Texts a link to this cart to `phoneNumber` so the recipient can review it and continue
   * checkout. The orders API records the share on the cart and caps how many times one cart can be
   * shared, so a share never writes over the cart's own customer phone number.
   */
  shareCart(
    cartId: string,
    phoneNumber: string,
    sendReadReceipt = false,
    senderPhoneNumber = "",
    message?: string,
  ): Observable<CartShareResult> {
    return this.postShare(`${this.endpoint}/${cartId}/share-sms`, {
      phoneNumber,
      sendReadReceipt,
      senderPhoneNumber,
      ...this.messageField(message),
    });
  }

  /**
   * Emails the same cart short link as <see cref="shareCart">. A blank message is omitted so the
   * API uses the default wording instead of rejecting the share.
   */
  shareCartByEmail(
    cartId: string,
    email: string,
    sendReadReceipt = false,
    senderPhoneNumber = "",
    message?: string,
    recipientName = "",
  ): Observable<CartShareResult> {
    const name = (recipientName || "").trim();
    return this.postShare(`${this.endpoint}/${cartId}/share-email`, {
      email: (email || "").trim(),
      sendReadReceipt,
      senderPhoneNumber,
      ...this.messageField(message),
      ...(name ? { recipientName: name } : {}),
    });
  }

  private postShare(path: string, body: Record<string, unknown>): Observable<CartShareResult> {
    let params = new HttpParams();
    if (this.user != null && this.user.profile != null) {
      params = params.append("partitionKey", this.user.profile.account_id);
    }

    return this.httpClient.post<CartShareResult>(`${this.baseUrl}${path}`, body, {
      headers: this.getAuth(),
      params,
    });
  }

  /** Omit a blank message so the API uses the default share wording instead of returning 400. */
  private messageField(message?: string): { message?: string } {
    const trimmedMessage = (message || "").trim();
    return trimmedMessage ? { message: trimmedMessage } : {};
  }

  /** Resolves a compact share code, recording the view and optional read receipt server-side. */
  resolveCartShare(shortCode: string): Observable<CartShareResolution> {
    return this.httpClient.get<CartShareResolution>(`${this.baseUrl}${this.endpoint}/share/${shortCode}`);
  }

  /** Records a short-link view after the recipient's cart copy has loaded successfully. */
  trackCartShareView(shortCode: string): Observable<CartShareResolution> {
    return this.httpClient.post<CartShareResolution>(
      `${this.baseUrl}${this.endpoint}/share/${shortCode}/view`,
      {},
      { headers: this.getAuth() },
    );
  }

  completeCart() {
    this.localStorageService.set(LocalStorageKeys.CartId, "");
    this.cart.activeStatus = "Inactive";
    this.updateSubscribers(this.cart);
  }

  wipeCart() {
    this.localStorageService.set(LocalStorageKeys.CartId, "");
    this.updateSubscribers(new Cart());
  }

  updateSubscribers(cart: Cart) {
    if (cart == null) {
      // there was a bug when removing the last item on the cart we have a null cart now so we need to initialize it again so people can add things to their cart again
      cart = new Cart();
    }

    this.cart = cart;
    this.cartBSubject.next(this.cart);
    if (cart?.id) {
      this.markCartHydrated();
    }
  }

  /** Lets the cart page tell loading-from-API apart from a genuinely empty cart. */
  markCartHydrated(): void {
    if (!this.cartHydratedBSubject.value) {
      this.cartHydratedBSubject.next(true);
    }
  }

  isWeeklyOrMonthly(duration: number): "day" | "week" | "month" {
    // Rentals shorter than 21 days are billed weekly; 21+ days are billed monthly.
    return isWeeklyDuration(duration) ? "week" : "month";
  }

  /**
   * Update transportation costs for cart items using AI when zip code changes
   */
  private updateTransportationCostsForItems(
    itemsToUpdate: { index: number; cartItem: CartItem }[],
    destination: Location | string,
  ): Observable<{ index: number; transportationEstimate: any }[]> {
    const updateObservables = itemsToUpdate.map(({ index, cartItem }) => {
      // Cart lines only store a city/state display name. Load the machine so the estimate gets the street address.
      return this.loadMachineLocation(cartItem.inventoryItem?.id).pipe(
        switchMap(machine => {
          const inventoryItem: InventoryItem = {
            id: cartItem.inventoryItem.id,
            primaryType: cartItem.inventoryItem.primaryType,
            weight: machine?.weight || cartItem.inventoryItem.weight,
            location: machine?.location,
          } as InventoryItem;

          return this.openAIService.estimateTransportationCost(inventoryItem, destination).pipe(
            map(transportationCost => {
              if (transportationCost) {
                const transportationEstimate = {
                  estimatedCost: transportationCost.average,
                  internalNotes: `$${transportationCost.min.toLocaleString()} - $${transportationCost.max.toLocaleString()}; ${transportationCost.explanation}`,
                  distance: {
                    value: transportationCost.distance.toString(),
                    unit: {
                      fullName: "Miles",
                      abbreviations: ["mls"],
                      system: "Standard",
                    },
                    system: "Standard",
                  },
                };
                return { index, transportationEstimate };
              }
              return { index, transportationEstimate: null };
            }),
            catchError(error => {
              console.error(`Failed to get transportation cost for item ${index}:`, error);
              return of({ index, transportationEstimate: null });
            }),
          );
        }),
      );
    });

    return forkJoin(updateObservables);
  }

  /** Machine search doc has the street address. Cart lines only keep a city/state display name. */
  private loadMachineLocation(inventoryItemId: string): Observable<InventoryItem | null> {
    if (!inventoryItemId || !environment?.azureSearchUrl || !environment?.azureSearchKey) {
      return of(null);
    }

    const headers = new HttpHeaders().set("api-key", environment.azureSearchKey);
    const key = encodeURIComponent(inventoryItemId);
    return this.httpClient
      .get<InventoryItem>(`${environment.azureSearchUrl}/indexes/machines/docs/${key}?api-version=2017-11-11`, { headers })
      .pipe(catchError(() => of(null)));
  }
}
